import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, MapPin, Menu, X, Store, ChefHat, Bike, Home, Users, Truck, Headphones, Quote } from 'lucide-react';
import namkeLogo      from '../assets/namke-logo-header.png';
import namkeLogoWhite from '../assets/namke-logo-header-white.png';
import './landing.css';

/* ─── data ──────────────────────────────────────────────────────────────── */

const DISHES = [
  { emoji: '🍲', name: 'Ndolé DG',        origin: 'Cameroun',      price: '14,50 €', time: '30 min' },
  { emoji: '🍗', name: 'Poulet DG',       origin: 'Cameroun',      price: '12,90 €', time: '25 min' },
  { emoji: '🫕', name: 'Fufu & Egusi',    origin: 'Nigéria',       price: '11,50 €', time: '35 min' },
  { emoji: '🥘', name: 'Thiéboudienne',   origin: 'Sénégal',       price: '13,90 €', time: '40 min' },
  { emoji: '🍜', name: 'Koki Maïs',       origin: 'Cameroun',      price: '8,50 €',  time: '20 min' },
  { emoji: '🥩', name: 'Brochettes Suya', origin: 'Nord-Cameroun', price: '9,90 €',  time: '15 min' },
];

const STEPS = [
  { Icon: Store,   num: '01', labelKey: 'step1Label', descKey: 'step1Desc' },
  { Icon: ChefHat, num: '02', labelKey: 'step2Label', descKey: 'step2Desc' },
  { Icon: Bike,    num: '03', labelKey: 'step3Label', descKey: 'step3Desc' },
  { Icon: Home,    num: '04', labelKey: 'step4Label', descKey: 'step4Desc' },
];

const MARKET_ICONS = [Store, Users, ChefHat, Truck, Headphones];

/* ─── Navbar ─────────────────────────────────────────────────────────────── */

function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  const links = [
    { to: '#plats',   label: t('landing.nav.ourDishes') },
    { to: '#comment', label: t('landing.nav.howItWorks') },
    { to: '#cuisiniers', label: t('landing.nav.becomeCook') },
  ];
  return (
    <nav className={`lnd-nav${scrolled ? ' lnd-nav--scrolled' : ''}`}>
      <div className="lnd-nav__inner">
        <Link to="/" className="lnd-nav__logo-wrap">
          <img src={namkeLogo} alt="Namke" className="lnd-nav__logo-img"/>
        </Link>
        <ul className="lnd-nav__links">
          {links.map(l => (
            <li key={l.to}><a className="lnd-nav__link" href={l.to}>{l.label}</a></li>
          ))}
        </ul>
        <div className="lnd-nav__cta">
          <Link className="lnd-btn-ghost" to="/login">{t('landing.nav.login')}</Link>
          <Link className="lnd-btn-nav-primary" to="/register">{t('landing.nav.order')}</Link>
        </div>
        <button className="lnd-nav__burger" onClick={() => setOpen(o => !o)} aria-label="menu">
          {open ? <X size={20}/> : <Menu size={20}/>}
        </button>
      </div>
      {open && (
        <div className="lnd-nav__mobile">
          {links.map(l => <a key={l.to} className="lnd-nav__mobile-link" href={l.to} onClick={() => setOpen(false)}>{l.label}</a>)}
          <div className="lnd-nav__mobile-ctas">
            <Link className="lnd-btn-secondary--onlight lnd-btn-secondary" to="/login" style={{ fontSize: '0.82rem', padding: '7px 14px' }}>{t('landing.nav.login')}</Link>
            <Link className="lnd-btn-nav-primary" to="/register" style={{ fontSize: '0.82rem', padding: '7px 14px' }}>{t('landing.nav.order')}</Link>
          </div>
        </div>
      )}
    </nav>
  );
}

/* ─── Hero ───────────────────────────────────────────────────────────────── */

function HeroSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [address, setAddress] = useState('');
  const featured = DISHES[1];

  const handleAddressSubmit = e => {
    e.preventDefault();
    navigate('/');
  };

  return (
    <section className="lnd-hero">
      <div className="lnd-hero__content">
        <span className="lnd-hero__badge">
          <span className="lnd-hero__badge-dot"/>{t('landing.hero.badge')}
        </span>
        <h1 className="lnd-hero__title">
          {t('landing.hero.title')}
        </h1>
        <p className="lnd-hero__sub">
          {t('landing.hero.subtitle')}
        </p>
        <form className="lnd-hero__address" onSubmit={handleAddressSubmit}>
          <MapPin size={18} className="lnd-hero__address-icon"/>
          <input
            type="text"
            className="lnd-hero__address-input"
            placeholder={t('landing.hero.addressPlaceholder')}
            value={address}
            onChange={e => setAddress(e.target.value)}
          />
          <button type="submit" className="lnd-hero__address-btn">
            {t('landing.hero.seeRestaurants')}
          </button>
        </form>
        <div className="lnd-hero__ctas">
          <Link to="/restaurant/register" className="lnd-btn-secondary">
            {t('landing.hero.haveRestaurant')}
          </Link>
        </div>
        <div className="lnd-hero__stats">
          {[['48 000+', t('landing.hero.statOrders')], ['340+', t('landing.hero.statRestaurants')], ['4.8/5', t('landing.hero.statRating')]].map(([v, l]) => (
            <div key={l} className="lnd-hero__stat"><strong>{v}</strong><span>{l}</span></div>
          ))}
        </div>
      </div>
      <div className="lnd-hero__visual">
        <div className="lnd-hero__halo"/>
        <div className="lnd-hero__frame">
          <span className="lnd-hero__frame-emoji">{featured.emoji}</span>
        </div>
        <div className="lnd-hero__float-card">
          <span className="lnd-hero__float-eyebrow">{t('landing.hero.todayLabel')}</span>
          <span className="lnd-hero__float-title">{featured.name}</span>
          <span className="lnd-hero__float-sub">{featured.origin} · {featured.time} · {featured.price}</span>
        </div>
      </div>
    </section>
  );
}

/* ─── Marquee tri-langue ─────────────────────────────────────────────────── */

function MarqueeStrip() {
  return (
    <section className="lnd-marquee-strip">
      <span>Mangez comme à la maison</span>
      <span className="lnd-marquee-strip__dot">·</span>
      <span className="lnd-marquee-strip__dim">Eating like at home</span>
      <span className="lnd-marquee-strip__dot">·</span>
      <span className="lnd-marquee-strip__dim">Essen wie zu Hause</span>
    </section>
  );
}

/* ─── Plats ───────────────────────────────────────────────────────────────── */

function PlatsSection() {
  const { t } = useTranslation();
  return (
    <section className="lnd-section" id="plats">
      <div className="lnd-sec-header">
        <span className="lnd-pill">{t('landing.dishes.pill')}</span>
        <h2 className="lnd-h2">{t('landing.dishes.titleLine1')}<br/>{t('landing.dishes.titleLine2')}</h2>
        <p className="lnd-lead lnd-lead--quote">{t('landing.dishes.quote')}</p>
      </div>
      <div className="lnd-dishes">
        {DISHES.map(d => (
          <div key={d.name} className="lnd-dish">
            <div className="lnd-dish__img">
              <span className="lnd-dish__emoji">{d.emoji}</span>
            </div>
            <div className="lnd-dish__body">
              <span className="lnd-dish__badge">{d.origin}</span>
              <div className="lnd-dish__name">{d.name}</div>
              <div className="lnd-dish__meta">
                <span>{d.time}</span>
                <span className="lnd-dish__price">{d.price}</span>
              </div>
              <button className="lnd-dish__btn">{t('landing.dishes.order')}</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Comment ça marche ──────────────────────────────────────────────────── */

function JourneySection() {
  const { t } = useTranslation();
  return (
    <section className="lnd-section lnd-section--alt" id="comment">
      <div className="lnd-journey-wrap">
        <div className="lnd-journey-info">
          <span className="lnd-pill">{t('landing.journey.pill')}</span>
          <h2 className="lnd-h2">{t('landing.journey.titleLine1')}<br/>{t('landing.journey.titleLine2')}</h2>
          <ol className="lnd-journey-list">
            {STEPS.map(s => (
              <li key={s.labelKey} className="lnd-journey-list__item">
                <span className="lnd-journey-list__icon"><s.Icon size={22} strokeWidth={1.7}/></span>
                <div className="lnd-journey-list__body">
                  <span className="lnd-journey-list__num">{t('landing.journey.step')} {s.num}</span>
                  <strong className="lnd-journey-list__label">{t(`landing.journey.${s.labelKey}`)}</strong>
                  <span className="lnd-journey-list__desc">{t(`landing.journey.${s.descKey}`)}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="lnd-journey-collage">
          <div className="lnd-journey-collage__tall"/>
          <div className="lnd-journey-collage__round"/>
          <div className="lnd-journey-collage__square"/>
        </div>
      </div>
    </section>
  );
}

/* ─── Positionnement ─────────────────────────────────────────────────────── */

function PositioningSection() {
  const { t } = useTranslation();
  return (
    <section className="lnd-positioning">
      <span className="lnd-positioning__eyebrow">{t('landing.positioning.eyebrow')}</span>
      <p className="lnd-positioning__title">
        {t('landing.positioning.titlePrefix')} <span className="lnd-positioning__accent">{t('landing.positioning.titleAccent')}</span>.
      </p>
      <p className="lnd-positioning__body">{t('landing.positioning.body')}</p>
    </section>
  );
}

/* ─── Place de marché (cuisiniers) ───────────────────────────────────────── */

function MarketplaceSection() {
  const { t } = useTranslation();
  const cards = [1, 2, 3, 4, 5].map(n => ({
    num: String(n).padStart(2, '0'),
    Icon: MARKET_ICONS[n - 1],
    title: t(`landing.marketplace.card${n}Title`),
    desc: t(`landing.marketplace.card${n}Desc`),
  }));
  return (
    <section className="lnd-section" id="cuisiniers">
      <div className="lnd-sec-header lnd-sec-header--split">
        <div>
          <span className="lnd-pill lnd-pill--terra">{t('landing.marketplace.eyebrow')}</span>
          <h2 className="lnd-h2">{t('landing.marketplace.title')}</h2>
        </div>
        <Link to="/restaurant/register" className="lnd-btn-secondary--onlight lnd-btn-secondary">
          {t('landing.marketplace.cta')}
        </Link>
      </div>
      <div className="lnd-market-grid">
        {cards.map((c, i) => (
          <article key={c.num} className={`lnd-market-card${i === 0 ? ' lnd-market-card--dark' : i === 3 ? ' lnd-market-card--olive' : ''}`}>
            <span className="lnd-market-card__icon"><c.Icon size={22} strokeWidth={1.7}/></span>
            <span className="lnd-market-card__num">{c.num}</span>
            <h3 className="lnd-market-card__title">{c.title}</h3>
            <p className="lnd-market-card__desc">{c.desc}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ─── Témoignages ─────────────────────────────────────────────────────────── */

function TestimonialsSection() {
  const { t } = useTranslation();
  return (
    <section className="lnd-testimonials">
      <blockquote className="lnd-testimonial">
        <Quote size={22} className="lnd-testimonial__mark"/>
        <p>{t('landing.testimonials.quote1')}</p>
        <footer><span className="lnd-testimonial__avatar" style={{ background: '#F4B81A' }}/>{t('landing.testimonials.author1')}</footer>
      </blockquote>
      <blockquote className="lnd-testimonial">
        <Quote size={22} className="lnd-testimonial__mark"/>
        <p>{t('landing.testimonials.quote2')}</p>
        <footer><span className="lnd-testimonial__avatar" style={{ background: '#72B744' }}/>{t('landing.testimonials.author2')}</footer>
      </blockquote>
    </section>
  );
}

/* ─── CTA ─────────────────────────────────────────────────────────────────── */

function CTASection() {
  const { t } = useTranslation();
  return (
    <div className="lnd-cta">
      <div className="lnd-cta__inner">
        <div className="lnd-cta__text">
          <span className="lnd-pill lnd-pill--light">{t('landing.cta.pill')}</span>
          <h2 className="lnd-cta__title">{t('landing.cta.titleLine1')}<br/>{t('landing.cta.titleLine2')}</h2>
          <p className="lnd-cta__sub">{t('landing.cta.subtitle')}</p>
          <div className="lnd-cta__btns">
            <Link to="/register" className="lnd-btn-primary">
              {t('landing.cta.orderNow')} <ArrowRight size={16}/>
            </Link>
            <Link to="/restaurant/register" className="lnd-btn-secondary">
              {t('landing.cta.openRestaurant')}
            </Link>
          </div>
        </div>
        <div className="lnd-cta__visual">
          <div className="lnd-cta__circle"/>
        </div>
      </div>
    </div>
  );
}

/* ─── Footer ──────────────────────────────────────────────────────────────── */

function LandingFooter() {
  const { t } = useTranslation();
  return (
    <footer className="lnd-footer">
      <div className="lnd-footer__inner">
        <div className="lnd-footer__brand">
          <div className="lnd-footer__logo-block">
            <img src={namkeLogoWhite} alt="Namke" className="lnd-footer__logo-img"/>
          </div>
          <p className="lnd-footer__tag">{t('landing.footer.tagline')}</p>
        </div>
        <div className="lnd-footer__cols">
          <div className="lnd-footer__col">
            <div className="lnd-footer__col-h">{t('landing.footer.platform')}</div>
            <Link to="/register" className="lnd-footer__link">{t('landing.footer.order')}</Link>
            <Link to="/login" className="lnd-footer__link">{t('landing.footer.login')}</Link>
            <Link to="/restaurant/register" className="lnd-footer__link">{t('landing.footer.openRestaurant')}</Link>
          </div>
          <div className="lnd-footer__col">
            <div className="lnd-footer__col-h">{t('landing.footer.about')}</div>
            <a href="#comment" className="lnd-footer__link">{t('landing.footer.howItWorks')}</a>
            <a href="#plats" className="lnd-footer__link">{t('landing.footer.ourDishes')}</a>
          </div>
          <div className="lnd-footer__col">
            <div className="lnd-footer__col-h">{t('landing.footer.legal')}</div>
            <a href="#" className="lnd-footer__link">{t('landing.footer.terms')}</a>
            <a href="#" className="lnd-footer__link">{t('landing.footer.privacy')}</a>
            <a href="#" className="lnd-footer__link">{t('landing.footer.contact')}</a>
          </div>
        </div>
      </div>
      <div className="lnd-footer__bottom">
        © {new Date().getFullYear()} Namke · Afrodine. {t('landing.footer.rights')}
      </div>
    </footer>
  );
}

/* ─── Landing ─────────────────────────────────────────────────────────────── */

export default function Landing() {
  return (
    <div className="lnd">
      <Navbar/>
      <HeroSection/>
      <MarqueeStrip/>
      <PlatsSection/>
      <JourneySection/>
      <PositioningSection/>
      <MarketplaceSection/>
      <TestimonialsSection/>
      <CTASection/>
      <LandingFooter/>
    </div>
  );
}
